import { plus } from '@0xtorch/big-decimal'
import type { Action } from '@0xtorch/core'
import {
  ApiSecretRequiredException,
  AssetNotFoundException,
} from '../../errors'
import type { FetchApiData } from '../../types'
import { createGetAsset } from '../../utils/createGetAsset'
import { bybitAccountCategory } from '../constants'
import { createClient } from '../createClient'
import { responseSchema } from './schema'

const QUOTE_CURRENCIES = [
  'USDT',
  'USDC',
  'USDE',
  'USD1',
  'EUR',
  'BRN',
  'PLN',
  'TRY',
  'SOL',
  'BTC',
  'ETH',
  'DAI',
  'BRZ',
]

const parseSymbol = (symbol: string): { base: string; quote: string } => {
  for (const quote of QUOTE_CURRENCIES) {
    if (symbol.endsWith(quote)) {
      const base = symbol.slice(0, -quote.length)
      if (base.length > 0) {
        return { base, quote }
      }
    }
  }
  throw new Error(`Unable to parse symbol: ${symbol}`)
}

export const fetchApiData: FetchApiData = async ({
  apiKey,
  apiSecret,
  assets,
  config,
  from,
  createHeaders,
  proxy,
  symbolAssetMap,
  to,
}) => {
  if (!apiSecret) {
    throw new ApiSecretRequiredException()
  }

  const getAsset = createGetAsset({ assets, symbolAssetMap })

  const client = createClient({
    apiKey,
    apiSecret,
    createHeaders,
    proxy,
  })

  const response = await client.run({
    method: 'GET',
    path: '/v5/execution/list',
    query: {
      category: 'spot',
      limit: config.limit.toString(),
      ...(from ? { startTime: from.toString() } : {}),
      ...(to ? { endTime: to.toString() } : {}),
    },
    responseSchema,
  })

  // Sort by execTime in descending order
  const sortedTrades = response.result.list.sort(
    (a, b) => Number(b.execTime) - Number(a.execTime),
  )

  // Data processing
  let fetchedFrom: number | undefined
  let fetchedTo: number | undefined
  const sources: string[] = []
  const actions: Action[] = []

  // Group trades by orderId and execTime
  const tradeGroups: Record<string, typeof sortedTrades> = {}

  for (const item of sortedTrades) {
    const execTime = Number(item.execTime)

    // Update fetchedFrom and fetchedTo
    fetchedFrom =
      fetchedFrom === undefined ? execTime : Math.min(fetchedFrom, execTime)
    fetchedTo =
      fetchedTo === undefined ? execTime : Math.max(fetchedTo, execTime)

    const groupKey = `${item.orderId}_${item.execTime}`
    if (!tradeGroups[groupKey]) {
      tradeGroups[groupKey] = []
    }
    tradeGroups[groupKey].push(item)
  }

  // Process each trade group
  for (const trades of Object.values(tradeGroups)) {
    if (trades.length === 0) continue

    const firstTrade = trades[0]
    const { base, quote } = parseSymbol(firstTrade.symbol)

    // Get assets
    const baseAsset = getAsset(base)
    const quoteAsset = getAsset(quote)
    const feeAsset = getAsset(firstTrade.feeCurrency)

    if (!baseAsset) {
      throw new AssetNotFoundException(base)
    }
    if (!quoteAsset) {
      throw new AssetNotFoundException(quote)
    }
    if (!feeAsset) {
      throw new AssetNotFoundException(firstTrade.feeCurrency)
    }

    // Calculate totals
    let totalExecQty = trades[0].execQty
    let totalExecValue = trades[0].execValue
    let totalExecFee = trades[0].execFee

    for (let i = 1; i < trades.length; i++) {
      totalExecQty = plus(totalExecQty, trades[i].execQty)
      totalExecValue = plus(totalExecValue, trades[i].execValue)
      totalExecFee = plus(totalExecFee, trades[i].execFee)
    }

    // Create action
    const action: Action = {
      comment: `Exec ID: ${trades.map((t) => t.execId).join(' / ')}`,
      evidence: 'system-rule',
      order: 0,
      source: `${bybitAccountCategory}_${config.type}_${firstTrade.orderId}_${firstTrade.execTime}`,
      type: 'trade',
      timestamp: Number(firstTrade.execTime),
      transfers: [
        {
          asset: baseAsset,
          amount: totalExecQty,
          direction: firstTrade.side === 'Buy' ? 'in' : 'out',
        },
        {
          asset: quoteAsset,
          amount: totalExecValue,
          direction: firstTrade.side === 'Buy' ? 'out' : 'in',
        },
        {
          asset: feeAsset,
          amount: totalExecFee,
          direction: 'out',
        },
      ],
    }

    actions.push(action)
  }

  // Sort actions by timestamp in descending order
  const sortedActions = actions.sort((a, b) => b.timestamp - a.timestamp)

  return {
    actions: sortedActions,
    count: response.result.list.length,
    from: fetchedFrom,
    sources,
    stopped: false,
    to: fetchedTo,
  }
}
